import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";
import { Check, Calendar, MapPin, Clock, Shield, ChevronRight, Loader2, Sparkles } from "lucide-react";
import { format, parseISO } from "date-fns";

const BookingConfirmation = () => {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/bookings/${id}`).then((r) => setB(r.data)).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  if (!b) return <div className="min-h-screen flex items-center justify-center">Booking not found</div>;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />

      <div className="max-w-3xl mx-auto px-5 md:px-8 py-12">
        <div className="text-center fade-up">
          <div className="inline-flex w-20 h-20 rounded-full bg-[#3DDC97] items-center justify-center animate-glow" data-testid="booking-success-icon">
            <Check size={36} className="text-[#0A0B10]" strokeWidth={3} />
          </div>
          <p className="text-[11px] uppercase tracking-widest font-bold text-[#2D7FF9] font-mono-num mt-6">— CONFIRMED · #{b.id.slice(0, 8).toUpperCase()}</p>
          <h1 className="font-display font-extrabold text-4xl md:text-5xl mt-3 tracking-tight">Your ride is booked.</h1>
          <p className="text-gray-600 mt-3 max-w-md mx-auto">We've reserved your bike. Show your booking ID at the pickup point.</p>
        </div>

        <div className="card-premium mt-10 overflow-hidden" data-testid="booking-details">
          <div className="relative aspect-[16/9] bg-gradient-to-br from-gray-50 to-gray-100">
            <img src={b.bike_snapshot.image} alt={b.bike_snapshot.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-5 left-6 text-white">
              <p className="text-xs uppercase tracking-widest font-bold opacity-80">{b.bike_snapshot.brand}</p>
              <h2 className="font-display font-extrabold text-3xl tracking-tight">{b.bike_snapshot.name}</h2>
            </div>
          </div>

          <div className="p-7 grid sm:grid-cols-2 gap-5">
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Pickup</p>
              <p className="font-display font-bold text-lg flex items-center gap-2 font-mono-num"><Calendar size={16} className="text-[#3DDC97]" />{format(parseISO(b.start_date), "dd MMM, HH:mm")}</p>
            </div>
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Drop</p>
              <p className="font-display font-bold text-lg flex items-center gap-2 font-mono-num"><Calendar size={16} className="text-[#2D7FF9]" />{format(parseISO(b.end_date), "dd MMM, HH:mm")}</p>
            </div>
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Location</p>
              <p className="font-display font-bold text-lg flex items-center gap-2"><MapPin size={16} />{b.pickup_point}, <span className="capitalize">{b.city}</span></p>
            </div>
            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Duration</p>
              <p className="font-display font-bold text-lg flex items-center gap-2"><Clock size={16} /><span className="font-mono-num">{b.total_hours}h · {b.plan}</span></p>
            </div>
          </div>

          <div className="px-7 pb-7">
            <div className="p-5 rounded-2xl bg-gradient-to-br from-[#3DDC97]/5 to-[#2D7FF9]/5 border border-[#3DDC97]/20">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Rental duration</span>
                <span className="font-semibold text-ink">{b.total_hours}h</span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-gray-600 flex items-center gap-1.5"><Shield size={12} />Refundable deposit</span>
                <span className="font-semibold text-ink">Included</span>
              </div>
              <div className="h-px bg-black/5 my-3" />
              <div className="flex items-baseline justify-between">
                <span className="font-semibold">Status</span>
                <span className="font-display font-bold text-2xl text-[#0d8f5b]">Confirmed</span>
              </div>
              <p className="text-xs text-gray-500 mt-2 flex items-center gap-1.5"><Sparkles size={11} className="text-[#3DDC97]" />Personalised quote will be shared with you</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
          <Link to="/dashboard" data-testid="goto-dashboard-btn" className="btn-primary justify-center">
            View all bookings <ChevronRight size={16} />
          </Link>
          <Link to="/bikes" data-testid="book-another-btn" className="px-6 py-3.5 rounded-full font-semibold border border-gray-200 hover:border-gray-300 text-center bg-white">
            Book another ride
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BookingConfirmation;
